import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Row,
  Col,
  Spinner,
  Badge,
  Card,
  CardBody,
  InputGroup,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';
import AssetModal from './AssetModal';
import { formatEnumName } from '../constants/utils';
import usePermission from '../Hooks/usePermission';
import { WorkOrderStatusBadge, AssetListTable } from './Common';

const LOCATION_GRADIENT = 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)';
const BREADCRUMB_VISIBLE_TAIL = 3; // collapse ancestors past 3 levels with "…", mirrors asset breadcrumb

const LocationModal = ({
  isOpen,
  toggle,
  title,
  mainData,
  relatedData,
  loading,
  formatCreatedDate,
  onTicketClick,
  locationData = null,
  onEditLocation = null,
  onLocationUpdated = null,
  initialActiveTab = null,
  onCreateSubLocation = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isRequestor } = usePermission();

  const [activeTab, setActiveTab] = useState('info');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Local copy of the viewed location — swapped in-place when user clicks a sub-location in the tree.
  const [currentLocation, setCurrentLocation] = useState(locationData);
  // Work orders for the currently-viewed location. Seeds from the relatedData prop on first open;
  // re-fetched internally whenever the user navigates to a sub-location via the tree.
  const [workOrders, setWorkOrders] = useState(relatedData || []);
  const [workOrdersLoading, setWorkOrdersLoading] = useState(false);

  // Ancestor chain for the breadcrumb. Lazy-walked via /get-by-id on each level.
  const [ancestors, setAncestors] = useState([]);

  // Tree state — nodes expanded in the Sub Locations tab, lazy-loaded children cache, in-flight fetches.
  const [expandedNodeIds, setExpandedNodeIds] = useState(() => new Set());
  const [childrenByParentId, setChildrenByParentId] = useState({});
  const [loadingNodeIds, setLoadingNodeIds] = useState(() => new Set());
  const [hoveredNodeId, setHoveredNodeId] = useState(null);

  // Sub-location delete confirmation
  const [subLocationDeleteModal, setSubLocationDeleteModal] = useState({ isOpen: false, subLocation: null });
  const [deletingSubLocation, setDeletingSubLocation] = useState(false);

  // Assets at this location
  const [locationAssets, setLocationAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [lastFetchedAssetsForId, setLastFetchedAssetsForId] = useState(null);

  // Nested asset modal (when user clicks an asset in the Assets tab)
  const [assetModal, setAssetModal] = useState({ isOpen: false, userData: null, loading: false });

  // Service requests (requestor only)
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loadingServiceRequests, setLoadingServiceRequests] = useState(false);
  const [selectedServiceRequest, setSelectedServiceRequest] = useState(null);

  const user = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = user?.activeOrganizationId ?? null;

  const subLocations = currentLocation?.subLocations || [];
  const headerName =
    currentLocation?.locationName ||
    currentLocation?.name ||
    (typeof mainData === 'string' ? mainData : mainData?.locationName) ||
    title;

  // Sync incoming locationData prop into local state when the modal is opened on a new location.
  useEffect(() => {
    if (isOpen) {
      setCurrentLocation(locationData);
      setWorkOrders(relatedData || []);
    }
  }, [isOpen, locationData, relatedData]);

  // Reset transient state on close.
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('info');
      setSearchTerm('');
      setSelectedStatus('');
      setExpandedNodeIds(new Set());
      setChildrenByParentId({});
      setLoadingNodeIds(new Set());
      setHoveredNodeId(null);
      setAncestors([]);
      setLocationAssets([]);
      setAssetsLoading(false);
      setLastFetchedAssetsForId(null);
      setAssetModal({ isOpen: false, userData: null, loading: false });
      setSubLocationDeleteModal({ isOpen: false, subLocation: null });
      setServiceRequests([]);
      setSelectedServiceRequest(null);
    }
  }, [isOpen]);

  // Honor initialActiveTab when the modal opens (e.g. from the list-row "N Sub-Locations" chip).
  useEffect(() => {
    if (isOpen && initialActiveTab) {
      setActiveTab(initialActiveTab);
    }
  }, [isOpen, initialActiveTab]);

  // Reset tree state whenever the viewed location changes (open or in-place swap).
  useEffect(() => {
    setExpandedNodeIds(new Set());
    setChildrenByParentId({});
    setLoadingNodeIds(new Set());
  }, [currentLocation?.id]);

  // Lazy-walk ancestors: starting from currentLocation, follow parentLocation/parentLocationId until null.
  // Collapses to "…" past BREADCRUMB_VISIBLE_TAIL levels in render.
  useEffect(() => {
    let cancelled = false;
    const walk = async () => {
      if (!currentLocation?.id) {
        setAncestors([]);
        return;
      }
      const chain = [];
      let cursor = currentLocation.parentLocation || null;
      let safetyCounter = 0;
      // If parentLocation object missing but parentLocationId present, fetch it.
      if (!cursor && currentLocation.parentLocationId) {
        try {
          const { data } = await api.get(`organization-location/get-by-id?id=${currentLocation.parentLocationId}`);
          cursor = data?.response || null;
        } catch (err) {
          console.error('Failed to fetch parent location for breadcrumb:', err);
        }
      }
      while (cursor && safetyCounter < 20) {
        chain.unshift(cursor);
        if (!cursor.parentLocationId) break;
        try {
          const { data } = await api.get(`organization-location/get-by-id?id=${cursor.parentLocationId}`);
          cursor = data?.response || null;
        } catch (err) {
          console.error('Failed to walk ancestor:', err);
          break;
        }
        safetyCounter += 1;
      }
      if (!cancelled) setAncestors(chain);
    };
    walk();
    return () => { cancelled = true; };
    // currentLocation.parentLocation is read inside but is only an optimization (use prefetched parent
    // when present). The walk is keyed on the location identity (id + parentLocationId).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation?.id, currentLocation?.parentLocationId]);

  // Swap the location rendered by this modal in-place — used by sub-location name clicks in the tree.
  const swapViewedLocation = useCallback(async (loc) => {
    if (!loc?.id) return;
    setActiveTab('info');
    setCurrentLocation(loc);
    try {
      const { data } = await api.get(`organization-location/get-by-id?id=${loc.id}`);
      if (data?.response) setCurrentLocation(data.response);
    } catch (err) {
      console.error('Failed to fetch full location on swap:', err);
    }
    // Re-fetch work orders for the swapped location.
    if (loc.locationName && organizationId) {
      setWorkOrdersLoading(true);
      try {
        const { data } = await api.get('asset-issues/organization/filtered', {
          params: { organizationLocation: loc.locationName, organizationId: organizationId },
        });
        setWorkOrders(data?.response || []);
      } catch (err) {
        console.error('Failed to fetch work orders for swapped location:', err);
        setWorkOrders([]);
      } finally {
        setWorkOrdersLoading(false);
      }
    }
  }, [organizationId]);

  // Lazy-fetch a node's children when first expanded. Cached afterwards.
  const fetchNodeChildren = useCallback(async (nodeId) => {
    if (childrenByParentId[nodeId] !== undefined) return;
    setLoadingNodeIds((prev) => { const next = new Set(prev); next.add(nodeId); return next; });
    try {
      const { data } = await api.get(`organization-location/get-by-id?id=${nodeId}`);
      const children = data?.response?.subLocations || [];
      setChildrenByParentId((prev) => ({ ...prev, [nodeId]: children }));
    } catch (err) {
      console.error('Error fetching sub-locations for node', nodeId, err);
      setChildrenByParentId((prev) => ({ ...prev, [nodeId]: [] }));
    } finally {
      setLoadingNodeIds((prev) => { const next = new Set(prev); next.delete(nodeId); return next; });
    }
  }, [childrenByParentId]);

  const toggleTreeNode = useCallback((nodeId) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
        fetchNodeChildren(nodeId);
      }
      return next;
    });
  }, [fetchNodeChildren]);

  const handleSubLocationClick = useCallback((sub) => {
    swapViewedLocation(sub);
  }, [swapViewedLocation]);

  const handleCreateUnderNode = useCallback((parentNode) => {
    if (!parentNode?.id) return;
    toggle();
    if (typeof onCreateSubLocation === 'function') {
      onCreateSubLocation(parentNode);
    } else {
      navigate(`/locations?createSubLocationOf=${parentNode.id}`);
    }
  }, [toggle, onCreateSubLocation, navigate]);

  const handleCreateSubLocationClick = useCallback(() => {
    if (!currentLocation?.id) return;
    toggle();
    if (typeof onCreateSubLocation === 'function') {
      onCreateSubLocation(currentLocation);
    } else {
      navigate(`/locations?createSubLocationOf=${currentLocation.id}`);
    }
  }, [currentLocation, toggle, onCreateSubLocation, navigate]);

  const handleEditSubLocation = useCallback((sub) => {
    if (typeof onEditLocation === 'function') {
      toggle();
      onEditLocation(sub);
    }
  }, [toggle, onEditLocation]);

  const requestDeleteSubLocation = useCallback((sub) => {
    setSubLocationDeleteModal({ isOpen: true, subLocation: sub });
  }, []);

  const confirmDeleteSubLocation = useCallback(async () => {
    const sub = subLocationDeleteModal.subLocation;
    if (!sub?.id) return;
    setDeletingSubLocation(true);
    try {
      await api.delete(`organization-location/delete?id=${sub.id}`);
      // Refetch the current location so the tree reflects the change.
      if (currentLocation?.id) {
        const { data } = await api.get(`organization-location/get-by-id?id=${currentLocation.id}`);
        if (data?.response) setCurrentLocation(data.response);
      }
      // Drop any cached children that included the deleted node.
      setChildrenByParentId({});
      setExpandedNodeIds(new Set());
      setSubLocationDeleteModal({ isOpen: false, subLocation: null });
      if (typeof onLocationUpdated === 'function') onLocationUpdated();
    } catch (err) {
      console.error('Error deleting sub-location:', err);
    } finally {
      setDeletingSubLocation(false);
    }
  }, [subLocationDeleteModal.subLocation, currentLocation?.id, onLocationUpdated]);

  // Fetch assets at the currently-viewed location when the Assets tab is active.
  const fetchAssetsByLocation = useCallback(async (locationName, locId) => {
    if (!locationName || !organizationId) return;
    setAssetsLoading(true);
    try {
      const { data } = await api.get(
        `asset/all?currentLocation=${encodeURIComponent(locationName)}&organizationId=${organizationId}`,
      );
      setLocationAssets(data?.response?.[0]?.assets || []);
      setLastFetchedAssetsForId(locId);
    } catch (err) {
      console.error('Error fetching assets for location:', err);
      setLocationAssets([]);
      setLastFetchedAssetsForId(locId);
    } finally {
      setAssetsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (isOpen && activeTab === 'assets' && currentLocation?.locationName) {
      if (lastFetchedAssetsForId !== currentLocation.id) {
        fetchAssetsByLocation(currentLocation.locationName, currentLocation.id);
      }
    }
  }, [isOpen, activeTab, currentLocation?.id, currentLocation?.locationName, lastFetchedAssetsForId, fetchAssetsByLocation]);

  // Fetch service requests when the requestor tab is active.
  const fetchServiceRequests = useCallback(async () => {
    if (!isRequestor || !currentLocation?.locationName) return;
    setLoadingServiceRequests(true);
    try {
      const { data } = await api.get('service-request/my-requests?count=0');
      const allRequests = data?.response || [];
      setServiceRequests(allRequests.filter((req) => req.locationName === currentLocation.locationName));
    } catch (err) {
      console.error('Error fetching service requests:', err);
      setServiceRequests([]);
    } finally {
      setLoadingServiceRequests(false);
    }
  }, [isRequestor, currentLocation?.locationName]);

  useEffect(() => {
    if (isOpen && activeTab === 'serviceRequests') {
      fetchServiceRequests();
    }
  }, [isOpen, activeTab, fetchServiceRequests]);

  // Fetch work orders when an asset is opened from the Assets tab (so AssetModal sees its issues).
  const fetchAssetWorkOrders = useCallback(async (assetId) => {
    if (!assetId || !organizationId) return [];
    try {
      const { data } = await api.get('asset-issues/organization/filtered', {
        params: { assetId, organizationId: organizationId, count: 9999 },
      });
      const issues = data?.response || [];
      setAssetModal((prev) => ({
        ...prev,
        userData: prev.userData ? { ...prev.userData, reportedIssues: issues } : prev.userData,
      }));
      return issues;
    } catch (err) {
      console.error('Error fetching asset work orders:', err);
      return [];
    }
  }, [organizationId]);

  const handleAssetClick = useCallback(async (asset) => {
    setAssetModal({ isOpen: true, userData: asset, loading: false });
    await fetchAssetWorkOrders(asset.id);
  }, [fetchAssetWorkOrders]);

  // Filter work orders by search/status (matches DetailsModal behavior).
  const filteredWorkOrders = useMemo(() => {
    return (workOrders || []).filter((item) => {
      const matchesSearch = searchTerm === '' ||
        item.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.asset?.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.issueType?.some((typ) => typ.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = selectedStatus === '' || item.status === selectedStatus;
      return matchesSearch && matchesStatus;
    });
  }, [workOrders, searchTerm, selectedStatus]);

  // ── Renderers ─────────────────────────────────────────────────────────────

  const renderBreadcrumb = () => {
    if (!ancestors.length) return null;
    const total = ancestors.length;
    const showCollapse = total > BREADCRUMB_VISIBLE_TAIL;
    const visible = showCollapse ? ancestors.slice(total - BREADCRUMB_VISIBLE_TAIL) : ancestors;
    const hidden = showCollapse ? ancestors.slice(0, total - BREADCRUMB_VISIBLE_TAIL) : [];

    return (
      <div className="d-flex align-items-center flex-wrap gap-1 mb-3 small text-muted">
        <FeatherIcon icon="map-pin" size={12} className="me-1" />
        {showCollapse && (
          <>
            <span
              role="button"
              className="text-primary"
              style={{ cursor: 'pointer' }}
              onClick={() => swapViewedLocation(hidden[0])}
              title={hidden.map((h) => h.locationName).join(' / ')}
            >
              …
            </span>
            <FeatherIcon icon="chevron-right" size={12} />
          </>
        )}
        {visible.map((node, idx) => (
          <React.Fragment key={node.id}>
            <span
              role="button"
              className="text-primary"
              style={{ cursor: 'pointer' }}
              onClick={() => swapViewedLocation(node)}
            >
              {node.locationName}
            </span>
            {idx < visible.length - 1 && <FeatherIcon icon="chevron-right" size={12} />}
            {idx === visible.length - 1 && <FeatherIcon icon="chevron-right" size={12} />}
          </React.Fragment>
        ))}
        <span className="fw-semibold text-dark">{currentLocation?.locationName}</span>
      </div>
    );
  };

  const renderInfoTab = () => (
    <div className="p-4">
      <Card className="border-0 shadow-sm mb-4">
        <CardBody className="p-4">
          {renderBreadcrumb()}
          <div className="text-center mb-4">
            <div
              className="location-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ width: '80px', height: '80px', background: LOCATION_GRADIENT }}
            >
              <FeatherIcon icon="map-pin" size={32} className="text-white" />
            </div>
            <Badge color="info" className="badge-soft">
              <FeatherIcon icon="map-pin" size={12} className="me-1" />
              {currentLocation?.childLocation
                ? t('business.locations.sub_location', 'Sub-Location')
                : t('shared.location', 'Location')}
            </Badge>
          </div>
          <h3 className="mb-4 fw-bold text-dark text-center">{headerName}</h3>
          <Row>
            <Col md={4}>
              <div className="detail-item mb-3">
                <div className="d-flex align-items-center mb-2">
                  <FeatherIcon icon="home" size={16} className="text-muted me-2" />
                  <span className="detail-label fw-semibold text-muted">{t('shared.organization', 'Organization')}</span>
                </div>
                <span className="detail-value fs-6 text-dark ms-4">
                  {currentLocation?.organization?.name
                    || currentLocation?.parentLocation?.organization?.name
                    || relatedData?.[0]?.organization?.name
                    || t('shared.not_specified', 'Not specified')}
                </span>
              </div>
            </Col>
            <Col md={4}>
              <div className="detail-item mb-3">
                <div className="d-flex align-items-start mb-2">
                  <FeatherIcon icon="map" size={16} className="text-muted me-2 mt-1" />
                  <span className="detail-label fw-semibold text-muted">{t('shared.address', 'Address')}</span>
                </div>
                <span className="detail-value fs-6 text-dark ms-4">
                  {currentLocation?.locationAddress
                    || relatedData?.[0]?.organization?.address
                    || t('business.components.details_modal.address_not_available', 'Address not available')}
                </span>
              </div>
            </Col>
            <Col md={4}>
              <div className="detail-item mb-3">
                <div className="d-flex align-items-start mb-2">
                  <FeatherIcon icon="phone" size={16} className="text-muted me-2 mt-1" />
                  <span className="detail-label fw-semibold text-muted">{t('shared.contact_number', 'Contact Number')}</span>
                </div>
                <span className="detail-value fs-6 text-dark ms-4">
                  {currentLocation?.organization?.contactNumber
                    || currentLocation?.parentLocation?.organization?.contactNumber
                    || relatedData?.[0]?.organization?.contactNumber
                    || t('business.components.details_modal.contact_number_not_available', 'Contact Number not available')}
                </span>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );

  const renderTreeNode = (node, depth = 0) => {
    const expanded = expandedNodeIds.has(node.id);
    const cached = childrenByParentId[node.id];
    const nodeLoading = loadingNodeIds.has(node.id);
    const knownChildren = cached !== undefined ? cached : (node.subLocations || []);
    const showChevron = (cached === undefined && (node.subLocations?.length ?? 1) > 0)
      || (cached !== undefined && cached.length > 0);
    const isHovered = hoveredNodeId === node.id;

    return (
      <div key={node.id}>
        <div
          className="d-flex align-items-center gap-2 py-2 pe-2 rounded"
          style={{
            paddingLeft: depth * 40 + 8,
            background: isHovered ? '#f8f9fa' : 'transparent',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={() => setHoveredNodeId(node.id)}
          onMouseLeave={() => setHoveredNodeId((prev) => (prev === node.id ? null : prev))}
        >
          {showChevron ? (
            <button
              type="button"
              className="btn btn-sm p-0 border-0 bg-transparent"
              style={{ width: 24, height: 24, lineHeight: 1 }}
              onClick={() => toggleTreeNode(node.id)}
              aria-label={expanded ? t('shared.collapse', 'Collapse') : t('shared.expand', 'Expand')}
            >
              {nodeLoading
                ? <Spinner size="sm" />
                : <FeatherIcon icon={expanded ? 'chevron-down' : 'chevron-right'} size={16} />}
            </button>
          ) : (
            <span style={{ display: 'inline-block', width: 24 }} />
          )}
          <i className="bx bx-sitemap text-info" style={{ fontSize: 18 }} />
          <span
            role="button"
            className="text-primary fw-medium flex-grow-1 text-truncate"
            style={{ cursor: 'pointer' }}
            onClick={() => handleSubLocationClick(node)}
            title={node.locationName}
          >
            {node.locationName}
          </span>
          {node.locationAddress && (
            <small className="text-muted text-truncate d-none d-md-inline" style={{ maxWidth: 220 }}>
              {node.locationAddress}
            </small>
          )}
          {isHovered && !isRequestor && (
            <button
              type="button"
              className="btn btn-sm btn-link text-primary p-0 d-inline-flex align-items-center gap-1"
              onClick={() => handleCreateUnderNode(node)}
              title={t('business.locations.create_sub_location', 'Create Sub-Location')}
              style={{ textDecoration: 'none' }}
            >
              <FeatherIcon icon="plus-circle" size={14} />
              <span style={{ fontSize: 12 }}>{t('business.locations.sub_location', 'Sub-Location')}</span>
            </button>
          )}
          {!isRequestor && (
            <div className="d-flex gap-1">
              <Button
                color="outline-warning"
                size="sm"
                className="btn-sm-icon"
                onClick={() => handleEditSubLocation(node)}
                title={t('shared.edit', 'Edit')}
                style={{ padding: '4px 8px' }}
              >
                <FeatherIcon icon="edit" size={12} />
              </Button>
              <Button
                color="outline-danger"
                size="sm"
                className="btn-sm-icon"
                onClick={() => requestDeleteSubLocation(node)}
                title={t('shared.delete', 'Delete')}
                style={{ padding: '4px 8px' }}
              >
                <FeatherIcon icon="trash-2" size={12} />
              </Button>
            </div>
          )}
        </div>
        {expanded && (
          <>
            {knownChildren.length === 0 && cached !== undefined && !nodeLoading && (
              <div
                className="text-muted small py-1"
                style={{ paddingLeft: (depth + 1) * 40 + 32 }}
              >
                {t('business.locations.no_nested_sub_locations', 'No further sub-locations.')}
              </div>
            )}
            {knownChildren.map((child) => renderTreeNode(child, depth + 1))}
          </>
        )}
      </div>
    );
  };

  const renderSubLocationsTab = () => (
    <div className="p-4">
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4">
          {/* Header Section */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div className="info-icon me-3">
                <div
                  className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <i className="bx bx-sitemap text-info fs-5" />
                </div>
              </div>
              <div>
                <h5 className="mb-1 fw-bold">
                  {t('shared.sub_locations', 'Sub Locations')}
                  {subLocations.length > 0 && (
                    <span className="text-muted ms-2">({subLocations.length})</span>
                  )}
                </h5>
                <p className="text-muted small mb-0">
                  {t('business.components.location_modal.sub_locations.subtitle', 'Manage sub-locations under this parent location')}
                </p>
              </div>
            </div>
            {!isRequestor && (
              <Button
                color="info"
                size="sm"
                onClick={handleCreateSubLocationClick}
                className="d-flex align-items-center gap-2"
              >
                <FeatherIcon icon="plus" size={14} />
                {t('business.locations.create_sub_location', 'Create Sub-Location')}
              </Button>
            )}
          </div>

          {/* Sub Locations Tree */}
          <div style={{ maxHeight: 'calc(90vh - 400px)', overflow: 'auto' }}>
            {subLocations.length > 0 ? (
              <div>{subLocations.map((node) => renderTreeNode(node, 0))}</div>
            ) : (
              <div className="text-center py-5">
                <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                  <i className="bx bx-sitemap text-muted fs-1" />
                </div>
                <h5 className="text-muted">
                  {t('business.components.details_modal.sub_locations.empty_title', 'No sub locations found')}
                </h5>
                <p className="text-muted mb-0">
                  {t('business.components.location_modal.sub_locations.empty_message', 'No sub-locations have been added for this location')}
                </p>
                {!isRequestor && (
                  <Button
                    color="info"
                    className="mt-3"
                    onClick={handleCreateSubLocationClick}
                  >
                    <FeatherIcon icon="plus" size={14} className="me-2" />
                    {t('business.components.location_modal.sub_locations.add_first_button', 'Add First Sub-Location')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );

  const renderAssetsTab = () => (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <FeatherIcon icon="package" size={20} className="text-primary me-2" />
          <h5 className="mb-0 fw-bold">{t('shared.assets', 'Assets')}</h5>
          {locationAssets?.length > 0 && (
            <Badge color="info" pill className="ms-2">{locationAssets.length}</Badge>
          )}
        </div>
        <button
          className="btn btn-info btn-sm d-flex align-items-center gap-2"
          onClick={() => fetchAssetsByLocation(currentLocation?.locationName, currentLocation?.id)}
          disabled={assetsLoading}
        >
          <FeatherIcon icon="refresh-cw" size={14} />
          {t('shared.refresh', 'Refresh')}
        </button>
      </div>
      <AssetListTable
        data={locationAssets}
        excludeColumns={['location']}
        onAssetClick={handleAssetClick}
        loading={assetsLoading}
        emptyMessage={t('shared.no_assets_found', 'No assets found')}
        emptySubMessage={t('business.components.details_modal.assets.empty_message', 'No assets are currently assigned to this location')}
      />
    </div>
  );

  const renderHistoryTab = () => {
    const isLoading = loading || workOrdersLoading;
    return (
      <div className="p-4">
        {workOrders?.length > 0 && (
          <Card className="border-0 bg-light mb-4">
            <CardBody className="p-3">
              <Row className="g-3 align-items-end">
                <Col md={10}>
                  <InputGroup size="sm">
                    <span className="input-group-text bg-white border-end-0">
                      <FeatherIcon icon="search" size={14} className="text-muted" />
                    </span>
                    <Input
                      type="text"
                      placeholder={t('business.components.details_modal.history.search_placeholder', `Search {{terms}}...`, {
                        terms: t('business.components.details_modal.history.search_terms.location', 'tickets, assets, issues'),
                      })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="border-start-0"
                      style={{ fontSize: '14px' }}
                    />
                  </InputGroup>
                </Col>
                <Col md={2}>
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={() => { setSearchTerm(''); setSelectedStatus(''); }}
                    disabled={!searchTerm && !selectedStatus}
                    className="w-100"
                  >
                    <FeatherIcon icon="x" size={14} />
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" size="lg" />
            <p className="text-muted mt-3">{t('business.components.details_modal.history.loading_message', 'Loading related data...')}</p>
          </div>
        ) : !workOrders || workOrders.length === 0 ? (
          <div className="text-center py-5">
            <div
              className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}
            >
              <FeatherIcon icon="inbox" size={24} className="text-muted" />
            </div>
            <h5 className="text-muted">{t('business.components.details_modal.history.empty_title', 'No related items found')}</h5>
            <p className="text-muted mb-0">{t('business.components.details_modal.history.empty_message', 'No work orders are associated with this {{type}}', { type: t('shared.location', 'location') })}</p>
          </div>
        ) : filteredWorkOrders.length === 0 && (searchTerm || selectedStatus) ? (
          <div className="text-center py-5">
            <div
              className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}
            >
              <FeatherIcon icon="search" size={24} className="text-muted" />
            </div>
            <h5 className="text-muted">{t('shared.no_matches_found', 'No matches found')}</h5>
            <p className="text-muted mb-0">{t('shared.adjust_filters_search', 'Try adjusting your search terms or filters')}</p>
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
            <table className="table table-hover modern-table mb-0">
              <thead className="table-light position-sticky top-0" style={{ zIndex: 5 }}>
                <tr>
                  <th className="border-0 py-3">
                    <div className="d-flex align-items-center">
                      <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                      <span className="fw-semibold">{t('shared.ticket', 'Ticket #')}</span>
                    </div>
                  </th>
                  <th className="border-0 py-3">
                    <div className="d-flex align-items-center">
                      <FeatherIcon icon="package" size={14} className="text-muted me-2" />
                      <span className="fw-semibold">{t('shared.asset', 'Asset')}</span>
                    </div>
                  </th>
                  <th className="border-0 py-3">
                    <div className="d-flex align-items-center">
                      <FeatherIcon icon="alert-circle" size={14} className="text-muted me-2" />
                      <span className="fw-semibold">{t('shared.issue_type', 'Issue Type')}</span>
                    </div>
                  </th>
                  <th className="border-0 py-3">
                    <div className="d-flex align-items-center">
                      <FeatherIcon icon="flag" size={14} className="text-muted me-2" />
                      <span className="fw-semibold">{t('shared.status', 'Status')}</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filteredWorkOrders.map((item, index) => (
                    <motion.tr
                      key={item.id || `wo-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05, type: 'spring', stiffness: 300 }}
                      whileHover={{ backgroundColor: '#f8f9fa' }}
                      className="cursor-pointer"
                    >
                      <td className="py-3">
                        <span
                          className="ticket-link fw-semibold text-primary"
                          onClick={() => onTicketClick && onTicketClick(item.ticketNumber)}
                          style={{ cursor: 'pointer' }}
                        >
                          {item.ticketNumber}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          <FeatherIcon icon="package" size={16} className="text-muted me-2" />
                          <span>{item.asset?.assetName || t('business.components.details_modal.assets.unknown_asset', 'Unknown Asset')}</span>
                        </div>
                      </td>
                      <td className="py-3">
                        {item?.issueType?.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {item.issueType.slice(0, 2).map((it, idx) => (
                              <Badge key={idx} color="light" className="text-dark small">{it.replace(/_/g, ' ')}</Badge>
                            ))}
                            {item.issueType.length > 2 && (
                              <Badge color="secondary" className="small">+{item.issueType.length - 2}</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">{t('shared.n_a', 'N/A')}</span>
                        )}
                      </td>
                      <td className="py-3"><WorkOrderStatusBadge status={item.status} /></td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderServiceRequestsTab = () => (
    <div className="p-4">
      {selectedServiceRequest ? (
        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            <div className="d-flex align-items-center mb-4">
              <Button color="link" className="p-0 me-3 text-muted" onClick={() => setSelectedServiceRequest(null)}>
                <FeatherIcon icon="arrow-left" size={20} />
              </Button>
              <div>
                <h5 className="mb-1 fw-bold">{selectedServiceRequest.requestNumber || t('shared.request_details', 'Request Details')}</h5>
                <p className="text-muted small mb-0">{selectedServiceRequest.title}</p>
              </div>
            </div>
            <Row className="g-4">
              <Col md={6}>
                <div className="bg-light rounded p-3 h-100">
                  <h6 className="text-primary mb-3 fw-semibold">
                    <FeatherIcon icon="info" size={16} className="me-2" />
                    {t('shared.basic_information', 'Basic Information')}
                  </h6>
                  <div className="mb-3">
                    <label className="text-muted small">{t('shared.title', 'Title')}</label>
                    <div className="fw-medium">{selectedServiceRequest.title}</div>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small">{t('shared.status', 'Status')}</label>
                    <div>
                      <Badge color={
                        selectedServiceRequest.status === 'PENDING' ? 'info'
                          : selectedServiceRequest.status === 'APPROVED' ? 'success'
                          : selectedServiceRequest.status === 'DECLINED' ? 'danger'
                          : selectedServiceRequest.status === 'DONE' ? 'primary' : 'secondary'
                      }>
                        {selectedServiceRequest.status
                          ? t(`shared.status_${selectedServiceRequest.status.toLowerCase()}`, formatEnumName(selectedServiceRequest.status))
                          : t('shared.unknown', 'UNKNOWN')}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light rounded p-3 h-100">
                  <h6 className="text-primary mb-3 fw-semibold">
                    <FeatherIcon icon="map-pin" size={16} className="me-2" />
                    {t('shared.asset_location', 'Asset & Location')}
                  </h6>
                  <div className="mb-3">
                    <label className="text-muted small">{t('shared.asset', 'Asset')}</label>
                    <div className="fw-medium">{selectedServiceRequest.asset?.assetName || '-'}</div>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small">{t('shared.location', 'Location')}</label>
                    <div className="fw-medium">{selectedServiceRequest.locationName || '-'}</div>
                  </div>
                  <div className="mb-0">
                    <label className="text-muted small">{t('shared.created_at', 'Created At')}</label>
                    <div className="fw-medium">{formatCreatedDate?.(selectedServiceRequest.createdAt)}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            <div className="d-flex align-items-center mb-4">
              <div className="info-icon me-3">
                <div
                  className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon icon="clipboard" size={20} className="text-primary" />
                </div>
              </div>
              <div>
                <h5 className="mb-1 fw-bold">{t('shared.my_service_requests', 'My Service Requests')}</h5>
                <p className="text-muted small mb-0">{t('business.components.details_modal.service_requests.my_requests_subtitle', 'Service requests you created for this location')}</p>
              </div>
            </div>
            {loadingServiceRequests ? (
              <div className="text-center py-5">
                <Spinner color="primary" size="lg" />
                <p className="text-muted mt-3">{t('business.components.asset_modal.service_requests.loading_message', 'Loading service requests...')}</p>
              </div>
            ) : serviceRequests.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>{t('shared.request_number_short', 'Request #')}</th>
                      <th>{t('shared.title', 'Title')}</th>
                      <th>{t('shared.status', 'Status')}</th>
                      <th>{t('shared.asset', 'Asset')}</th>
                      <th>{t('shared.created', 'Created')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceRequests.map((req, index) => (
                      <motion.tr
                        key={req.id || `sr-${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <td>
                          <span
                            className="fw-semibold text-primary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedServiceRequest(req)}
                          >
                            {req.requestNumber || `#${index + 1}`}
                          </span>
                        </td>
                        <td className="fw-medium">{req.title}</td>
                        <td>
                          <Badge color={
                            req.status === 'PENDING' ? 'info'
                              : req.status === 'APPROVED' ? 'success'
                              : req.status === 'DECLINED' ? 'danger'
                              : req.status === 'DONE' ? 'primary' : 'secondary'
                          }>
                            {req.status ? t(`shared.status_${req.status.toLowerCase()}`, formatEnumName(req.status)) : t('shared.unknown', 'UNKNOWN')}
                          </Badge>
                        </td>
                        <td className="text-muted">{req.asset?.assetName || '-'}</td>
                        <td className="text-muted">{formatCreatedDate?.(req.createdAt)}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5">
                <div
                  className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}
                >
                  <FeatherIcon icon="clipboard" size={24} className="text-muted" />
                </div>
                <h5 className="text-muted">{t('shared.no_service_requests', 'No service requests')}</h5>
                <p className="text-muted mb-0">{t('business.components.details_modal.service_requests.empty_message', "You haven't created any service requests for this location")}</p>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );

  // ── Top-level render ──────────────────────────────────────────────────────

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: -50 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 25 } },
    exit: { opacity: 0, scale: 0.8, y: 50, transition: { duration: 0.2 } },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          toggle={toggle}
          size="xl"
          centered
          className="modern-modal"
          backdrop="static"
          style={{ maxHeight: '90vh' }}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="modal-content border-0 shadow-lg"
            style={{ borderRadius: '12px', overflow: 'hidden' }}
          >
            <div
              className="modal-header border-0 text-white position-sticky d-flex justify-content-between align-items-center"
              style={{ top: 0, zIndex: 10, background: LOCATION_GRADIENT, padding: '1.5rem' }}
            >
              <div className="d-flex align-items-center">
                <div className="header-icon me-3">
                  <div
                    className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '48px', height: '48px' }}
                  >
                    <FeatherIcon icon="map-pin" size={24} className="text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="modal-title mb-1 text-white">{headerName}</h4>
                  <p className="mb-0 text-white-50 small text-capitalize">
                    {t('business.components.details_modal.header_subtitle', '{{type}} Details & Related Information', {
                      type: t('shared.location', 'Location'),
                    })}
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 ms-auto">
                {!isRequestor && onEditLocation && currentLocation && (
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                    onClick={() => onEditLocation(currentLocation)}
                    title={t('shared.edit_location', 'Edit Location')}
                    style={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      color: '#495057',
                      fontWeight: '500',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <FeatherIcon icon="edit-3" size={16} />
                    <span>{t('shared.edit_location', 'Edit Location')}</span>
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={toggle}
                  aria-label={t('shared.close', 'Close')}
                />
              </div>
            </div>

            <div
              className="modal-body p-0"
              style={{ maxHeight: 'calc(90vh - 140px)', overflow: 'auto' }}
            >
              <div className="p-4">
                <Nav tabs className="nav-tabs-custom mb-0">
                  <NavItem>
                    <NavLink
                      className={activeTab === 'info' ? 'active' : ''}
                      onClick={() => setActiveTab('info')}
                      style={{ cursor: 'pointer' }}
                    >
                      <FeatherIcon icon="info" size={16} className="me-2" />
                      {t('shared.info', 'Info')}
                    </NavLink>
                  </NavItem>
                  {!isRequestor && (
                    <NavItem>
                      <NavLink
                        className={activeTab === 'history' ? 'active' : ''}
                        onClick={() => setActiveTab('history')}
                        style={{ cursor: 'pointer' }}
                      >
                        <FeatherIcon icon="list" size={16} className="me-2" />
                        {t('shared.work_orders_history', 'Work Orders History')}
                        {workOrders?.length > 0 && (
                          <Badge color="primary" pill className="ms-2">{filteredWorkOrders.length}</Badge>
                        )}
                      </NavLink>
                    </NavItem>
                  )}
                  {!isRequestor && (
                    <NavItem>
                      <NavLink
                        className={activeTab === 'sublocations' ? 'active' : ''}
                        onClick={() => setActiveTab('sublocations')}
                        style={{ cursor: 'pointer' }}
                      >
                        <FeatherIcon icon="map-pin" size={16} className="me-2" />
                        {t('shared.sub_locations', 'Sub Locations')}
                        {subLocations?.length > 0 && (
                          <Badge color="success" pill className="ms-2">{subLocations.length}</Badge>
                        )}
                      </NavLink>
                    </NavItem>
                  )}
                  {!isRequestor && (
                    <NavItem>
                      <NavLink
                        className={activeTab === 'assets' ? 'active' : ''}
                        onClick={() => setActiveTab('assets')}
                        style={{ cursor: 'pointer' }}
                      >
                        <FeatherIcon icon="package" size={16} className="me-2" />
                        {t('shared.assets', 'Assets')}
                        {locationAssets?.length > 0 && (
                          <Badge color="info" pill className="ms-2">{locationAssets.length}</Badge>
                        )}
                      </NavLink>
                    </NavItem>
                  )}
                  {isRequestor && (
                    <NavItem>
                      <NavLink
                        className={activeTab === 'serviceRequests' ? 'active' : ''}
                        onClick={() => setActiveTab('serviceRequests')}
                        style={{ cursor: 'pointer' }}
                      >
                        <FeatherIcon icon="clipboard" size={16} className="me-2" />
                        {t('shared.service_requests', 'Service Requests')}
                        {serviceRequests.length > 0 && (
                          <Badge color="primary" pill className="ms-2">{serviceRequests.length}</Badge>
                        )}
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="info">{renderInfoTab()}</TabPane>
                  <TabPane tabId="history">{renderHistoryTab()}</TabPane>
                  <TabPane tabId="sublocations">{renderSubLocationsTab()}</TabPane>
                  <TabPane tabId="assets">{renderAssetsTab()}</TabPane>
                  {isRequestor && <TabPane tabId="serviceRequests">{renderServiceRequestsTab()}</TabPane>}
                </TabContent>
              </div>
            </div>
          </motion.div>
        </Modal>
      )}

      {/* Sub-location delete confirmation */}
      <Modal
        isOpen={subLocationDeleteModal.isOpen}
        toggle={() => setSubLocationDeleteModal({ isOpen: false, subLocation: null })}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title">{t('business.components.details_modal.sub_locations.delete_title', 'Confirm Delete Sub Location')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setSubLocationDeleteModal({ isOpen: false, subLocation: null })}
            aria-label={t('shared.close', 'Close')}
          />
        </div>
        <div className="modal-body">
          <p>
            {t('business.components.details_modal.sub_locations.delete_confirmation',
              'Are you sure you want to delete the sub location "{{name}}"? This action cannot be undone.',
              { name: subLocationDeleteModal.subLocation?.locationName })}
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setSubLocationDeleteModal({ isOpen: false, subLocation: null })}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={confirmDeleteSubLocation}
            disabled={deletingSubLocation}
          >
            {deletingSubLocation ? (
              <>
                <Spinner size="sm" className="me-1" />
                {t('shared.deleting', 'Deleting...')}
              </>
            ) : (
              t('shared.delete', 'Delete')
            )}
          </button>
        </div>
      </Modal>

      {/* Nested asset modal — opens when an asset row in the Assets tab is clicked. */}
      {assetModal.isOpen && (
        <AssetModal
          isOpen={assetModal.isOpen}
          toggle={() => setAssetModal((prev) => ({ ...prev, isOpen: false }))}
          userData={assetModal.userData}
          loading={assetModal.loading}
          formatCreatedDate={formatCreatedDate}
          onTicketClick={onTicketClick}
          fetchUserWorkOrders={fetchAssetWorkOrders}
        />
      )}
    </AnimatePresence>
  );
};

export default LocationModal;
